import backpackSdk from "@src/global_functions/backpackSdk/sdk";
import { useDetailedSiteSummaryQuery } from "@src/global_functions/sites/sitesQueries";
import { Skeleton } from "@src/straps/base";
import Breadcrumbs, {
  Crumb,
} from "@src/straps/base/navigation/Breadcrumbs/Breadcrumbs";
import { ReactNode } from "react";
import { generatePath, matchPath, useLocation } from "react-router-dom";
import { useSiteQueryParams } from "./SiteEquipment/siteEquipmentQueryParamUtils";

export const EQUIPMENT_PATHS = {
  PORTFOLIO_PAGE: "/equipment",
  SITE_PAGE: "/site/:siteId/equipment",
  ADD_EQUIPMENT_PAGE: "/site/:siteId/equipment/create",
  EQUIPMENT_PAGE: "/site/:siteId/equipment/:equipmentId",
} as const;

export default function EquipmentBreadcrumbs({
  ActionButtonsNode,
  EquipmentNameNode,
}: {
  readonly ActionButtonsNode?: ReactNode;
  readonly EquipmentNameNode?: ReactNode;
}) {
  const { siteId, equipmentId } = useEquipmentParamPathMatches();

  const { data: siteData } = useDetailedSiteSummaryQuery({
    siteId: siteId,
  });

  const siteQueryParams = useSiteQueryParams();

  const { data: equipmentData } =
    backpackSdk.equipment.equipment.useQuery(equipmentId);

  const crumbs: Crumb[] = [
    {
      name: "Equipment",
      route: EQUIPMENT_PATHS.PORTFOLIO_PAGE,
      childRoutes: siteId
        ? [
            {
              name: siteData ? (
                siteData.name
              ) : (
                <Skeleton className="h-full w-32" />
              ),
              route: generatePath(EQUIPMENT_PATHS.SITE_PAGE, {
                siteId,
              }),
              search: siteQueryParams,
              childRoutes: [
                {
                  name: EquipmentNameNode ?? "Add Equipment",
                  route: generatePath(EQUIPMENT_PATHS.ADD_EQUIPMENT_PAGE, {
                    siteId,
                  }),
                },
                ...(typeof equipmentId === "number"
                  ? [
                      {
                        name: equipmentData ? (
                          EquipmentNameNode ?? equipmentData.name
                        ) : (
                          <Skeleton className="h-full w-32" />
                        ),
                        route: generatePath(EQUIPMENT_PATHS.EQUIPMENT_PAGE, {
                          siteId,
                          equipmentId,
                        }),
                      },
                    ]
                  : []),
              ],
            },
          ]
        : undefined,
    },
  ];

  return (
    <div className="mb-5 flex w-full items-center justify-between border-b border-straps-accent-3 pb-3">
      <Breadcrumbs crumbs={crumbs} />
      {ActionButtonsNode}
    </div>
  );
}

function useEquipmentParamPathMatches() {
  const location = useLocation();

  const siteMatch = matchPath<Record<string, string>>(location.pathname, {
    path: [EQUIPMENT_PATHS.SITE_PAGE, EQUIPMENT_PATHS.EQUIPMENT_PAGE],
  });
  const siteId = isNaN(Number(siteMatch?.params.siteId))
    ? null
    : Number(siteMatch?.params.siteId);

  const equipmentIdMatch = matchPath<Record<string, string>>(
    location.pathname,
    {
      path: EQUIPMENT_PATHS.EQUIPMENT_PAGE,
    }
  );

  const equipmentId = isNaN(Number(equipmentIdMatch?.params.equipmentId))
    ? null
    : Number(equipmentIdMatch?.params.equipmentId);

  return {
    siteId,
    equipmentId,
  };
}
